import React, { useEffect, useRef, useCallback } from 'react';
import { FaSearch, FaCrosshairs, FaCamera } from 'react-icons/fa';
import { createRoot } from 'react-dom/client';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import "./Mapview.css";


function UnifiedControlPanel({ 
  toggleSearchPanel, 
  isSearchPanelOpen
}) {
  const map = useMap();
  const controlRef = useRef(null);
  const containerRef = useRef(null);
  const screenshoterRef = useRef(null);

  useEffect(() => {
    if (!map || screenshoterRef.current) return;

    screenshoterRef.current = L.simpleMapScreenshoter({
      hidden: true,
      screenName: "capture_map",
      mimeType: "image/png",
      caption: null,
    }).addTo(map);

    return () => {
      if (screenshoterRef.current) {
        map.removeLayer(screenshoterRef.current);
        screenshoterRef.current = null;
      }
    };
  }, [map]);

  const handleCaptureA4 = async () => {
    try {
      if (!screenshoterRef.current) {
        console.error("Screenshoter non initialisé");
        return;
      }
      const A4_WIDTH = 600;
      const A4_HEIGHT = 1123;

      const blob = await screenshoterRef.current.takeScreen({
        width: A4_WIDTH,
        height: A4_HEIGHT,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CAPTURE_TITRE.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur capture A4:", err);
      alert("Erreur lors de la capture A4");
    }
  };

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) {
      alert("La géolocalisation n'est pas supportée.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;

        const circle = L.circle([latitude, longitude], {
          radius: 40,
          color: "#2980b9",
          weight: 2,
          fillColor: "#2980b9",
          fillOpacity: 0.2,
        }).addTo(map);

        const marker = L.circleMarker([latitude, longitude], {
          radius: 10,
          color: "#2570a1ff",
          fillColor: "#2980b9",
          fillOpacity: 1,
        }).addTo(map);

        map.fitBounds(circle.getBounds());                                
      },
      (err) => alert("Impossible de récupérer votre position : " + err.message)
    );
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const createControl = () => {
      const container = L.DomUtil.create(
      "div",
      "leaflet-control leaflet-bar custom-top-control"
    );
      container.style.cssText = `
        background: rgba(255, 255, 255, 0.95);
        padding: 8px;
        border-radius: 10px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        z-index: 1000;
        
        
      `;
    
      containerRef.current = container;
                                                      
      const searchBtn = L.DomUtil.create('div', 'control-btn', container);
      searchBtn.style.cssText = `
        cursor: pointer;
        background: ${isSearchPanelOpen ? '#e3f2fd' : 'white'};
        border-radius: 8px;
        width: 34px;
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        
      `;
      searchBtn.title = 'Recherche';
      createRoot(searchBtn).render(<FaSearch color={isSearchPanelOpen ? "#1e88e5" : "#2980b9"} size={18} />);
      
      L.DomEvent.on(searchBtn, 'click', (e) => {
        L.DomEvent.stopPropagation(e);
        toggleSearchPanel();
      });

      const locateBtn = L.DomUtil.create('div', 'control-btn', container);
      locateBtn.style.cssText = `
        cursor: pointer;
        background: white;
        border-radius: 8px;
        width: 34px;
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        
      `;
      locateBtn.title = 'Ma position';
      createRoot(locateBtn).render(<FaCrosshairs color="#2980b9" size={18} />);
      
      L.DomEvent.on(locateBtn, 'click', (e) => {
        L.DomEvent.stopPropagation(e);
        handleLocate();
      });

      const captureBtn = L.DomUtil.create('div', 'control-btn', container);
      captureBtn.style.cssText = `
        cursor: pointer;
        background: white;
        border-radius: 8px;
        width: 34px;
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        
      `;
      captureBtn.title = 'Capture A4';
      createRoot(captureBtn).render(<FaCamera color="#2980b9" size={18} />);
      
      L.DomEvent.on(captureBtn, 'click', (e) => {
        L.DomEvent.stopPropagation(e);
        handleCaptureA4(); 
      });

      [searchBtn, locateBtn, captureBtn].forEach(btn => {
      btn.onmouseenter = () => {
        btn.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
        btn.style.background = '#e3f2fd';
      };
      btn.onmouseleave = () => {
        btn.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)';
        btn.style.background = 'white';
      };
    });

      const ControlClass = L.Control.extend({
        options: { position: 'topleft' },
        onAdd: () => container,
      });

      return new ControlClass();
    };

    if (!controlRef.current) {
      controlRef.current = createControl();
      map.addControl(controlRef.current);
    }

    const updateSearchButtonStyle = () => {
      if (!containerRef.current) return;
      const searchBtn = containerRef.current.querySelector('.control-btn:first-child');
      if (searchBtn) {
        searchBtn.style.background = isSearchPanelOpen ? '#e3f2fd' : 'white';
        const icon = searchBtn.querySelector('svg');
        if (icon) {
          icon.style.color = isSearchPanelOpen ? "#1e88e5" : "#2980b9";
        }
      }
    };

    updateSearchButtonStyle();

    return () => {
      if (controlRef.current) {
        map.removeControl(controlRef.current);
        controlRef.current = null;
      }
    };
 }, [map]);

  return null;
}

export default UnifiedControlPanel;